import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { CategorySearchSelect } from 'views/DispatchImporters/CategoryManagement/CategorySearchSelect';
import { AddMaterialCategoryModal } from 'views/DispatchImporters/CategoryManagement/AddMaterialCategoryModal';
import { type MaterialCategoryRecord } from 'views/DispatchImporters/CategoryManagement/types';
import { type MaterialGradeRecord, type MakeModelRecord } from './types';

interface FormData {
  make: string;
  model: string;
  materialCategory: string;
  tonnage: string;
  volume: string;
}

interface FormErrors {
  make?: string;
  model?: string;
  materialCategory?: string;
  tonnage?: string;
  volume?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onAdd: (record: MaterialGradeRecord) => Promise<void>;
  makeModelOptions: MakeModelRecord[];
  materialCategories: MaterialCategoryRecord[];
  onRefetchMaterialCategories: () => Promise<void>;
}

const DEFAULT_FORM: FormData = {
  make: '',
  model: '',
  materialCategory: '',
  tonnage: '',
  volume: '',
};

export const AddMaterialGradeModal = ({
  open,
  onClose,
  onAdd,
  makeModelOptions,
  materialCategories,
  onRefetchMaterialCategories,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormData>(DEFAULT_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [addCategoryPrefill, setAddCategoryPrefill] = useState('');

  const uniqueMakes = Array.from(
    new Set(makeModelOptions.map((o) => o.make))
  ).sort((a, b) => a.localeCompare(b));

  const modelOptions = form.make
    ? Array.from(
        new Set(
          makeModelOptions
            .filter((o) => o.make?.toLowerCase() === form.make.toLowerCase())
            .map((o) => o.model)
        )
      ).sort((a, b) => a.localeCompare(b))
    : [];

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!form.make.trim()) next.make = t('mg_required');
    if (!form.model.trim()) next.model = t('mg_required');
    if (!form.materialCategory.trim()) next.materialCategory = t('mg_required');
    const t_ = parseFloat(form.tonnage);
    if (isNaN(t_) || t_ <= 0) next.tonnage = t('mg_must_be_positive_number');
    const v_ = parseFloat(form.volume);
    if (isNaN(v_) || v_ <= 0) next.volume = t('mg_must_be_positive_number');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleClose = () => {
    setForm(DEFAULT_FORM);
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onAdd({
        id: crypto.randomUUID(),
        make: form.make.trim(),
        model: form.model.trim(),
        materialCategory: form.materialCategory.trim(),
        tonnage: parseFloat(form.tonnage),
        volume: parseFloat(form.volume),
      });
    } catch {
      // error toasted by parent; modal stays open
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    (key: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th) => alpha(th.palette.primary.main, 0.15),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            pt: 2.5,
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
                border: '1px solid',
                borderColor: (th) => alpha(th.palette.primary.main, 0.25),
              }}
            >
              <CategoryOutlinedIcon
                sx={{ fontSize: 18, color: 'primary.main' }}
              />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {t('mg_add_title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('mg_add_subtitle')}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={uniqueMakes}
                value={form.make || null}
                onChange={(_e, selected) => {
                  setForm((prev) => ({
                    ...prev,
                    make: selected ?? '',
                    model: '',
                  }));
                  setErrors((prev) => ({ ...prev, make: undefined }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('mg_field_make')}
                    size="small"
                    error={!!errors.make}
                    helperText={errors.make}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={modelOptions}
                value={form.model || null}
                disabled={!form.make}
                onChange={(_e, selected) => {
                  setForm((prev) => ({ ...prev, model: selected ?? '' }));
                  setErrors((prev) => ({ ...prev, model: undefined }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('mg_field_model')}
                    size="small"
                    error={!!errors.model}
                    helperText={errors.model}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <CategorySearchSelect
                label={t('mg_field_material_category')}
                codes={materialCategories.map((m) => m.enumValue)}
                getLabel={(code) => code}
                value={form.materialCategory || null}
                onChange={(code) => {
                  setForm((prev) => ({
                    ...prev,
                    materialCategory: code ?? '',
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    materialCategory: undefined,
                  }));
                }}
                required
                error={errors.materialCategory}
                addNewLabel={t('mg_add_new_material_category')}
                onAddNew={(searchText) => {
                  setAddCategoryPrefill(searchText);
                  setAddCategoryOpen(true);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('mg_field_tonnage')}
                fullWidth
                size="small"
                type="number"
                value={form.tonnage}
                onChange={field('tonnage')}
                error={!!errors.tonnage}
                helperText={errors.tonnage}
                inputProps={{ step: 'any', min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('mg_field_volume')}
                fullWidth
                size="small"
                type="number"
                value={form.volume}
                onChange={field('volume')}
                error={!!errors.volume}
                helperText={errors.volume}
                inputProps={{ step: 'any', min: 0 }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: '1px solid',
            borderColor: (th) => alpha(th.palette.divider, 0.5),
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={submitting}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('mg_cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('mg_btn_add_submit')}
          </Button>
        </DialogActions>
      </Dialog>
      <AddMaterialCategoryModal
        open={addCategoryOpen}
        initialCode={addCategoryPrefill}
        onClose={() => {
          setAddCategoryOpen(false);
        }}
        onCreated={(created) => {
          setAddCategoryOpen(false);
          setForm((prev) => ({ ...prev, materialCategory: created.enumValue }));
          setErrors((prev) => ({ ...prev, materialCategory: undefined }));
          void onRefetchMaterialCategories();
        }}
      />
    </>
  );
};
